import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { OrganicMotion } from "./organic-motion.js";
import {
  ORGANIC_SHAPES,
  ORGANIC_STEM_PATH,
  buildOrganicEyePath,
  organicShapeToBezierPath,
  parseOrganicShapeIndex,
} from "./organic.js";

export interface OrganicBotStageProps {
  color: string;
  shapeIndex?: number;
  size?: number;
  className?: string;
  style?: CSSProperties;
  nameFocused?: boolean;
  nameLength?: number;
  replayTrigger?: number;
  onPoke?: () => void;
}

/**
 * Web & Desktop stage reproducing the iOS create-bot entrance & physics effect:
 * 1. 5-droplet inward convergence to seed circle (0-195ms)
 * 2. Elastic expansion overshoot ("pop")
 * 3. Happy moon smile (∩ ∩) & tilt (-11deg) entrance
 * 4. Pumpkin stem elastic growth
 * 5. Input text tracking & gaze following
 * 6. Interactive poke bounce
 */
export function OrganicBotStage({
  color,
  shapeIndex,
  size = 220,
  className,
  style,
  nameFocused = false,
  nameLength = 0,
  replayTrigger = 0,
  onPoke,
}: OrganicBotStageProps) {
  const resolvedShapeIndex =
    shapeIndex !== undefined ? shapeIndex : parseOrganicShapeIndex(color);
  const colorPart = color.split("::shape_")[0] || "#FF2D79";

  const motionRef = useRef<OrganicMotion | null>(null);
  if (!motionRef.current) {
    motionRef.current = new OrganicMotion(resolvedShapeIndex, colorPart);
  }

  const [frame, setFrame] = useState(() => motionRef.current!.frame);
  const rafId = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);

  // Sync prop changes into the physics clock
  useEffect(() => {
    motionRef.current?.changeShape(resolvedShapeIndex);
  }, [resolvedShapeIndex]);

  useEffect(() => {
    motionRef.current?.changeColor(colorPart);
  }, [colorPart]);

  useEffect(() => {
    motionRef.current?.setLook(nameFocused, nameLength);
  }, [nameFocused, nameLength]);

  useEffect(() => {
    if (replayTrigger > 0) {
      motionRef.current?.replay();
    }
  }, [replayTrigger]);

  // Initial replay with slight delay so dialog mounting animation doesn't clip inward droplets
  useEffect(() => {
    const timer = setTimeout(() => {
      motionRef.current?.replay();
    }, 120);
    return () => clearTimeout(timer);
  }, []);

  // Animation render loop
  useEffect(() => {
    let active = true;

    function loop(now: number) {
      if (!active) return;
      if (lastTimeRef.current !== null) {
        const delta = (now - lastTimeRef.current) / 1000;
        motionRef.current?.advance(delta);
        if (motionRef.current) {
          setFrame(motionRef.current.frame);
        }
      }
      lastTimeRef.current = now;
      rafId.current = requestAnimationFrame(loop);
    }

    rafId.current = requestAnimationFrame(loop);

    return () => {
      active = false;
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
      lastTimeRef.current = null;
    };
  }, []);

  const handlePoke = () => {
    motionRef.current?.poke();
    onPoke?.();
  };

  const bodyD = organicShapeToBezierPath(frame.points);
  const currentShape = ORGANIC_SHAPES[motionRef.current.shapeIndex]!;
  const hasStem = currentShape.hasStem;

  return (
    <div
      className={className}
      onClick={handlePoke}
      style={{
        position: "relative",
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        userSelect: "none",
        ...style,
      }}
    >
      {/* 1. Ambient radial color glow behind the bot */}
      <div
        style={{
          position: "absolute",
          width: size * 0.95,
          height: size * 0.95,
          borderRadius: "50%",
          background: `radial-gradient(circle at center, ${frame.color}40 0%, transparent 72%)`,
          transform: `translateY(${6 + frame.floatY * 0.5}px)`,
          transition: "background 0.3s ease",
          pointerEvents: "none",
        }}
      />

      {/* 2. Soft floor contact shadow */}
      <div
        style={{
          position: "absolute",
          bottom: size * 0.08,
          width: size * 0.55 * frame.shadowScale,
          height: size * 0.12 * frame.shadowScale,
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse at center, rgba(0, 0, 0, 0.28) 0%, transparent 72%)",
          transform: `scale(${frame.shadowScale})`,
          pointerEvents: "none",
        }}
      />

      {/* 3. Main vector canvas (200x200 standard unit box) */}
      <svg
        viewBox="0 0 200 200"
        width={size}
        height={size}
        aria-hidden="true"
        style={{ overflow: "visible", position: "relative", zIndex: 1 }}
      >
        {/* Inward converging droplets layer (during intro 0~210ms) */}
        {frame.droplets.map((d, index) => (
          <ellipse
            key={index}
            cx={d.center.x}
            cy={d.center.y}
            rx={d.radiusX}
            ry={d.radiusY}
            transform={`rotate(${(d.angle * 180) / Math.PI} ${d.center.x} ${d.center.y})`}
            fill={frame.color}
            fillOpacity={d.opacity}
          />
        ))}

        {/* Transformed body group: floatY, tilt rotation, scaleX/Y squish */}
        <g
          transform={`translate(100 100) translate(0 ${frame.floatY}) rotate(${frame.rotation}) scale(${frame.scaleX} ${frame.scaleY}) translate(-100 -100)`}
        >
          {/* Pumpkin stem (if shape has stem) */}
          {hasStem && frame.stemScale > 0.001 ? (
            <path
              d={ORGANIC_STEM_PATH}
              transform={`translate(100, ${frame.points[0]?.y ?? 40}) scale(${frame.stemScale})`}
              fill="#231F20"
            />
          ) : null}

          {/* Elastic organic body */}
          <path d={bodyD} fill={frame.color} />

          {/* Expressive face with smile, blink & gaze tracking */}
          {frame.faceScale > 0.001 ? (
            <g
              transform={`translate(${100 + frame.look.x} ${frame.eyeY + frame.look.y}) scale(${frame.faceScale}) translate(-100 -${frame.eyeY})`}
              fill={frame.eyeColor}
            >
              <path
                d={buildOrganicEyePath(
                  100 - frame.eyeGap,
                  frame.eyeY,
                  frame.smile,
                  frame.blink,
                  1.05,
                )}
              />
              <path
                d={buildOrganicEyePath(
                  100 + frame.eyeGap,
                  frame.eyeY,
                  frame.smile,
                  frame.blink,
                  1.05,
                )}
              />
            </g>
          ) : null}
        </g>
      </svg>
    </div>
  );
}
